/**
 * <AssignOnboardingToEmployee /> — manager assigns an onboarding template
 * to an existing employee (e.g. for re-training, role changes, or to give
 * an existing teammate the same paperwork new hires get).
 *
 * Route: /admin/onboarding/assign
 *
 * Flow:
 *   1. Manager picks an existing employee (employees not currently mid-
 *      onboarding) and a template.
 *   2. We call createOnboardingForEmployee which provisions checklist
 *      items, progress doc, sets status to 'onboarding', and notifies the
 *      employee.
 *   3. Redirect to the manager's view of that employee.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../../context/AppContext';
import { useToast } from '../../../components/Toast';
import {
  createOnboardingForEmployee,
  listActiveTemplates,
} from '../services/onboardingService';
import '../Onboarding.css';

export default function AssignOnboardingToEmployee() {
  const navigate = useNavigate();
  const toast = useToast();
  const { state } = useApp();
  const { employees } = state;

  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [templateId, setTemplateId] = useState('residential_cleaner_v1');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const list = await listActiveTemplates();
        setTemplates(list);
      } catch (e) {
        // Fall back to default option in select.
      }
    })();
  }, []);

  // Eligible = anyone not already mid-onboarding. We don't want to clobber
  // an in-flight checklist by re-running create-onboarding.
  const eligibleEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (employees || [])
      .filter((e) => e.status !== 'onboarding')
      .filter((e) => {
        if (!q) return true;
        const hay = `${e.firstName || ''} ${e.lastName || ''} ${e.email || ''}`.toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
  }, [employees, search]);

  const selectedEmployee = employees.find((e) => e.id === employeeId);

  const validate = () => {
    const errs = {};
    if (!employeeId) errs.employeeId = 'Pick an employee';
    if (!templateId) errs.templateId = 'Pick a template';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await createOnboardingForEmployee({ employeeId, templateId });
      toast.success(`Onboarding assigned to ${selectedEmployee?.firstName || 'employee'}`);
      navigate(`/admin/onboarding/${employeeId}`);
    } catch (err) {
      toast.error(err.message || 'Failed to assign onboarding');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="onboarding-shell">
      <h1 style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Assign onboarding</h1>
      <p style={{ color: '#555' }}>
        Assign an onboarding template (paperwork, policies, manager checks) to an existing
        employee. They'll get an email and SMS letting them know their checklist is ready.
      </p>

      <form onSubmit={onSubmit}>
        <div className="onboarding-form-row">
          <label>Find employee</label>
          <input
            type="text"
            placeholder="Search by name or email"
            value={search}
            onChange={(ev) => setSearch(ev.target.value)}
          />
        </div>

        <div className="onboarding-form-row">
          <label>Employee</label>
          <select
            value={employeeId}
            onChange={(ev) => {
              setEmployeeId(ev.target.value);
              setErrors({ ...errors, employeeId: null });
            }}
          >
            <option value="">— Select an employee —</option>
            {eligibleEmployees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.firstName} {e.lastName} {e.email ? `(${e.email})` : ''}
              </option>
            ))}
          </select>
          {eligibleEmployees.length === 0 && (
            <div style={{ color: '#777', fontSize: '0.85rem', marginTop: 4 }}>
              {employees.length === 0
                ? 'No employees yet. Add one from the Employees page.'
                : 'No eligible employees. Everyone matching is either mid-onboarding or filtered out.'}
            </div>
          )}
          {errors.employeeId && <div className="field-error">{errors.employeeId}</div>}
        </div>

        <div className="onboarding-form-row">
          <label>Onboarding template</label>
          <select
            value={templateId}
            onChange={(ev) => {
              setTemplateId(ev.target.value);
              setErrors({ ...errors, templateId: null });
            }}
          >
            {templates.length === 0 && (
              <option value="residential_cleaner_v1">Residential Cleaner v1</option>
            )}
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.name} ({t.version})</option>
            ))}
          </select>
          {errors.templateId && <div className="field-error">{errors.templateId}</div>}
        </div>

        {selectedEmployee && (
          <div style={{
            background: '#f6f6f1', border: '1px solid #e6e3d6', borderRadius: 6,
            padding: 10, fontSize: '0.85rem', color: '#555', marginBottom: 8,
          }}>
            Assigning will set <strong>{selectedEmployee.firstName} {selectedEmployee.lastName}</strong>'s
            status to <em>onboarding</em> until they finish the checklist, then it returns
            to <em>active</em> automatically.
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button
            type="button"
            className="onboarding-btn secondary"
            onClick={() => navigate('/admin/onboarding')}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="onboarding-btn"
            disabled={submitting || !employeeId}
            style={{ flex: 1 }}
          >
            {submitting ? 'Assigning…' : 'Assign onboarding'}
          </button>
        </div>
      </form>
    </div>
  );
}
